// src/pages/AdminRequestsPage.js — Bloque 4
import { useEffect, useState, useMemo } from 'react';
import api from '../api/api';
import { formatearFecha, esFechaDeCalendario } from '../utils/fechas';
import { servicesCatalog } from '../config/formsConfig';

const font = "Montserrat, sans-serif";
const inputSx = { padding: "7px 10px", borderRadius: 8, border: "1.5px solid rgba(0,0,0,0.12)", fontSize: 12, fontFamily: font, color: "#163244", background: "#fff", outline: "none", boxSizing: "border-box" };
const labelSx = { fontSize: 9, fontWeight: 700, color: "#7a93a4", textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 3 };

const STATUS_CFG = {
  created:   { label: "Creada",      color: "#163244", bg: "rgba(22,50,68,0.08)"   },
  pending:   { label: "Pendiente",   color: "#b06a00", bg: "rgba(176,106,0,0.08)"  },
  reviewing: { label: "En revisión", color: "#185FA5", bg: "rgba(24,95,165,0.08)"  },
  assigned:  { label: "Asignada",    color: "#0F6E56", bg: "rgba(15,110,86,0.08)"  },
  completed: { label: "Completada",  color: "#1b7f4e", bg: "rgba(27,127,78,0.08)"  },
  cancelled: { label: "Cancelada",   color: "#c0392b", bg: "rgba(192,57,43,0.08)"  },
};
const SVC_COLORS = ["#163244","#1b7f4e","#7b5ea7","#b06a00","#185FA5","#c0392b","#0F6E56","#854F0B","#2E4057","#5C6BC0","#00838F","#E65100"];
const MONTHS_ES  = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
const PAGE_SIZE  = 25;

const getSvcName  = (key) => servicesCatalog.find(s => s.slug === key)?.name || key;
const fmtLabel    = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const renderVal   = (val) => { if (val === null || val === undefined || val === '') return '—'; if (typeof val === 'boolean') return val ? 'Sí' : 'No'; if (Array.isArray(val)) return val.join(', '); if (esFechaDeCalendario(val)) return formatearFecha(val); if (typeof val === 'object') return Object.entries(val).map(([k,v]) => `${fmtLabel(k)}: ${v}`).join(' · '); return String(val); };
const parseData   = (raw) => { if (!raw) return {}; if (typeof raw === 'string') { try { return JSON.parse(raw); } catch { return {}; } } return raw; };

function StatusBadge({ status }) {
  const cfg = STATUS_CFG[status] || { label: status, color: "#7a93a4", bg: "#f0f5f8" };
  return <span style={{ padding: "2px 8px", borderRadius: 20, fontSize: 9, fontWeight: 700, color: cfg.color, background: cfg.bg, whiteSpace: "nowrap" }}>{cfg.label.toUpperCase()}</span>;
}

function Toast({ msg, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }, []);
  return <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: type === "error" ? "#c0392b" : "#163244", color: "#fff", padding: "11px 22px", borderRadius: 10, fontWeight: 600, fontSize: 13, zIndex: 999, boxShadow: "0 4px 20px rgba(0,0,0,0.2)", borderLeft: `4px solid ${type === "error" ? "#e57373" : "#E7A432"}`, fontFamily: font }}>{msg}</div>;
}

// ─── Drawer detalle solicitud ─────────────────────────────────────────────────
function RequestDrawer({ req, providers, onClose, onStatusChange, onAssign, assignLoading }) {
  const [status,   setStatus]   = useState(req.status);
  const [provSel,  setProvSel]  = useState(null);
  const [showAssign, setShowAssign] = useState(false);
  const common   = parseData(req.common_data);
  const specific = parseData(req.specific_data);
  const cfg      = STATUS_CFG[req.status] || STATUS_CFG.pending;

  const SKIP_COMMON = ["provincia","ciudad","fotos","terminos_aceptados"];

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, background: "rgba(22,50,68,0.55)", backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#fff", borderRadius: 18, width: "min(560px,100%)", maxHeight: "92vh", overflowY: "auto", boxShadow: "0 16px 60px rgba(22,50,68,0.22)", display: "flex", flexDirection: "column" }}>

        {/* Header */}
        <div style={{ background: "#163244", padding: "18px 22px", borderRadius: "18px 18px 0 0", display: "flex", justifyContent: "space-between", alignItems: "flex-start", position: "relative", overflow: "hidden", flexShrink: 0 }}>
          <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle at 90% 20%, rgba(231,164,50,0.15) 0%, transparent 55%)", pointerEvents: "none" }} />
          <div style={{ zIndex: 1 }}>
            <div style={{ color: "#E7A432", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", marginBottom: 3 }}>{getSvcName(req.service_key).toUpperCase()}</div>
            <div style={{ color: "#fff", fontWeight: 800, fontSize: 16, marginBottom: 6 }}>Solicitud #{req.id}</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <StatusBadge status={req.status} />
              {req.request_type === "urgent" && <span style={{ padding: "2px 8px", borderRadius: 20, fontSize: 9, fontWeight: 700, color: "#c0392b", background: "rgba(192,57,43,0.15)" }}>🚨 URGENTE</span>}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "#fff", width: 28, height: 28, borderRadius: "50%", cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1 }}>✕</button>
        </div>

        <div style={{ padding: "18px 22px", overflowY: "auto" }}>
          {/* Cliente */}
          <div style={{ marginBottom: 14 }}>
            <div style={labelSx}>Cliente</div>
            <div style={{ background: "#f5f7fa", borderRadius: 10, padding: "12px 14px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {[["Nombre", req.user_name], ["Email", req.user_email], ["Teléfono", req.user_phone || "—"], ["Fecha", new Date(req.created_at).toLocaleString("es-AR")]].map(([l,v]) => (
                <div key={l}><div style={{ fontSize: 9, color: "#aab8c2", fontWeight: 700, marginBottom: 2 }}>{l}</div><div style={{ fontSize: 12, fontWeight: 600, color: "#163244", wordBreak: "break-all" }}>{v}</div></div>
              ))}
              {(common.provincia || common.ciudad) && (
                <div style={{ gridColumn: "1/-1" }}>
                  <div style={{ fontSize: 9, color: "#aab8c2", fontWeight: 700, marginBottom: 2 }}>Ubicación</div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#163244" }}>📍 {[common.ciudad, common.provincia].filter(Boolean).join(", ")}</div>
                </div>
              )}
            </div>
          </div>

          {/* Detalles del servicio */}
          {Object.keys(specific).length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={labelSx}>Detalles del servicio</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {Object.entries(specific).map(([key, val]) => (
                  <div key={key} style={{ background: "#f0f4ff", borderRadius: 8, padding: "8px 12px", minWidth: 140, flex: key.includes("descripcion") ? "1 0 100%" : "0 0 auto" }}>
                    <div style={{ fontSize: 9, color: "#7a93a4", fontWeight: 700, marginBottom: 2 }}>{fmtLabel(key)}</div>
                    <div style={{ fontSize: 12, color: "#163244", fontWeight: 600 }}>{renderVal(val)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Info general */}
          {Object.keys(common).filter(k => !SKIP_COMMON.includes(k)).length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={labelSx}>Información general</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {Object.entries(common).filter(([k]) => !SKIP_COMMON.includes(k)).map(([key, val]) => (
                  <div key={key} style={{ background: "#f5f7fa", borderRadius: 8, padding: "8px 12px", minWidth: 140, flex: key === "descripcion" ? "1 0 100%" : "0 0 auto" }}>
                    <div style={{ fontSize: 9, color: "#7a93a4", fontWeight: 700, marginBottom: 2 }}>{fmtLabel(key)}</div>
                    <div style={{ fontSize: 12, color: "#163244", fontWeight: 600 }}>{renderVal(val)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Cambiar estado */}
          <div style={{ borderTop: "1px solid rgba(0,0,0,0.06)", paddingTop: 14, marginBottom: 10 }}>
            <div style={labelSx}>Cambiar estado</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <select value={status} onChange={e => { setStatus(e.target.value); onStatusChange(req.id, e.target.value); }} style={{ ...inputSx, cursor: "pointer" }}>
                {Object.entries(STATUS_CFG).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              {req.status === "assigned" && (
                <button onClick={() => setShowAssign(true)} style={{ padding: "7px 14px", borderRadius: 50, border: "1.5px solid rgba(15,110,86,0.3)", background: "transparent", color: "#0F6E56", fontFamily: font, fontWeight: 600, fontSize: 12, cursor: "pointer" }}>Reasignar prestador</button>
              )}
            </div>
          </div>

          {/* Asignación manual */}
          {showAssign && (
            <div style={{ background: "rgba(22,50,68,0.04)", borderRadius: 10, padding: "14px", marginBottom: 10 }}>
              <div style={labelSx}>Seleccionar prestador</div>
              <select style={{ ...inputSx, width: "100%", marginBottom: 10 }} value={provSel?.provider_id || ""} onChange={e => setProvSel(providers.find(p => String(p.provider_id) === e.target.value) || null)}>
                <option value="">— Seleccionar prestador —</option>
                {providers.map(p => <option key={p.provider_id} value={p.provider_id}>{p.display_name} — ⭐ {parseFloat(p.rating_avg || 0).toFixed(1)} · {p.jobs_completed || 0} trabajos</option>)}
              </select>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => setShowAssign(false)} style={{ flex: 1, padding: "9px", borderRadius: 50, border: "1.5px solid rgba(0,0,0,0.12)", background: "transparent", color: "#7a93a4", fontFamily: font, fontWeight: 600, fontSize: 12, cursor: "pointer" }}>Cancelar</button>
                <button onClick={() => onAssign(provSel)} disabled={!provSel || assignLoading} style={{ flex: 2, padding: "9px", borderRadius: 50, border: "none", background: "#163244", color: "#fff", fontFamily: font, fontWeight: 700, fontSize: 12, cursor: !provSel || assignLoading ? "not-allowed" : "pointer" }}>{assignLoading ? "Asignando…" : "Confirmar asignación"}</button>
              </div>
            </div>
          )}

          <button onClick={onClose} style={{ width: "100%", padding: "10px", borderRadius: 50, border: "1.5px solid rgba(0,0,0,0.12)", background: "transparent", color: "#7a93a4", fontFamily: font, fontWeight: 600, fontSize: 12, cursor: "pointer" }}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

// ─── Sidebar estadísticas ─────────────────────────────────────────────────────
function SidebarStats({ requests }) {
  const stats = useMemo(() => {
    const byStatus = {}, byService = {}, byMonth = {}, byType = { urgent: 0, quote: 0 };
    const now = new Date();
    requests.forEach(r => {
      byStatus[r.status] = (byStatus[r.status] || 0) + 1;
      const svc = getSvcName(r.service_key);
      byService[svc] = (byService[svc] || 0) + 1;
      const d = new Date(r.created_at);
      const mk = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
      byMonth[mk] = (byMonth[mk] || 0) + 1;
      if (r.request_type === "urgent") byType.urgent++; else byType.quote++;
    });
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(); d.setMonth(d.getMonth() - i);
      const mk = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
      months.push({ label: MONTHS_ES[d.getMonth()], value: byMonth[mk] || 0 });
    }
    const services = Object.entries(byService).sort((a,b)=>b[1]-a[1]).slice(0,8).map(([l,v])=>({label:l,value:v}));
    const total = requests.length || 1;
    const pctUrgente = Math.round((byType.urgent / total) * 100);
    return { byStatus, services, months, byType, pctUrgente, total };
  }, [requests]);

  const maxSvc   = Math.max(...stats.services.map(s=>s.value), 1);
  const maxMonth = Math.max(...stats.months.map(m=>m.value), 1);
  const SCard    = ({ title, children }) => (
    <div style={{ background: "#fff", borderRadius: 12, border: "1px solid rgba(0,0,0,0.07)", padding: "12px 14px", marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 10 }}>
        <div style={{ width: 3, height: 12, background: "#E7A432", borderRadius: 2 }} />
        <span style={{ fontSize: 11, fontWeight: 700, color: "#163244" }}>{title}</span>
      </div>
      {children}
    </div>
  );

  return (
    <div>
      {/* Por estado */}
      <SCard title="Por estado">
        {Object.entries(STATUS_CFG).map(([k,cfg]) => {
          const v = stats.byStatus[k] || 0;
          if (!v) return null;
          return (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", borderBottom: "1px solid rgba(0,0,0,0.04)", fontSize: 11 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ width: 7, height: 7, borderRadius: "50%", background: cfg.color }} />
                <span style={{ color: "#163244" }}>{cfg.label}</span>
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <div style={{ width: 50, height: 4, borderRadius: 2, background: "#f0f5f8", overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${(v/(stats.total||1))*100}%`, background: cfg.color, borderRadius: 2 }} />
                </div>
                <span style={{ fontWeight: 700, color: cfg.color, minWidth: 20, textAlign: "right" }}>{v}</span>
              </div>
            </div>
          );
        })}
      </SCard>

      {/* Servicios más solicitados */}
      <SCard title="Top servicios">
        {stats.services.map((s, i) => (
          <div key={s.label} style={{ marginBottom: 7 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
              <span style={{ fontSize: 10, fontWeight: 600, color: "#163244", maxWidth: "72%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.label}</span>
              <span style={{ fontSize: 10, fontWeight: 700, color: SVC_COLORS[i % SVC_COLORS.length] }}>{s.value}</span>
            </div>
            <div style={{ height: 4, borderRadius: 2, background: "#f0f5f8", overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${(s.value/maxSvc)*100}%`, borderRadius: 2, background: SVC_COLORS[i % SVC_COLORS.length], transition: "width 0.5s" }} />
            </div>
          </div>
        ))}
      </SCard>

      {/* Tipo urgente vs cotización */}
      <SCard title="Tipo de solicitud">
        <div style={{ display: "flex", gap: 8 }}>
          {[["🚨 Urgente", stats.byType.urgent, "#c0392b", stats.pctUrgente], ["💬 Cotización", stats.byType.quote, "#163244", 100-stats.pctUrgente]].map(([l,v,c,p]) => (
            <div key={l} style={{ flex: 1, background: "#f5f7fa", borderRadius: 9, padding: "10px 12px", textAlign: "center" }}>
              <div style={{ fontSize: 16, fontWeight: 900, color: c }}>{p}%</div>
              <div style={{ fontSize: 10, color: "#7a93a4", marginTop: 2 }}>{l}</div>
              <div style={{ fontSize: 10, color: "#aab8c2" }}>{v} solicitudes</div>
            </div>
          ))}
        </div>
      </SCard>

      {/* Evolución mensual */}
      <SCard title="Evolución mensual">
        <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 56 }}>
          {stats.months.map((m,i) => {
            const h = maxMonth > 0 ? Math.max((m.value/maxMonth)*44, m.value>0?3:0) : 0;
            return (
              <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, height: "100%", justifyContent: "flex-end" }}>
                {m.value > 0 && <div style={{ fontSize: 8, color: "#7a93a4", fontWeight: 600 }}>{m.value}</div>}
                <div style={{ width: "100%", borderRadius: "2px 2px 0 0", background: "#163244", height: `${h}px`, opacity: i === 5 ? 1 : 0.5 + (i/5)*0.5 }} />
                <div style={{ fontSize: 8, color: "#aab8c2", fontWeight: 600 }}>{m.label}</div>
              </div>
            );
          })}
        </div>
      </SCard>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function AdminRequestsPage() {
  const [requests,       setRequests]       = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState(null);
  const [selected,       setSelected]       = useState(null);
  const [search,         setSearch]         = useState('');
  const [statusFilter,   setStatusFilter]   = useState('');
  const [page,           setPage]           = useState(0);
  const [toast,          setToast]          = useState(null);
  const [providers,      setProviders]      = useState([]);
  const [assignLoading,  setAssignLoading]  = useState(false);

  const showToast = (msg, type="success") => setToast({ msg, type });

  useEffect(() => { fetchRequests(); }, []);

  const fetchRequests = async () => {
    setLoading(true); setError(null);
    try { const res = await api.get('/api/service-requests'); setRequests(res.data); }
    catch (err) { setError(err.response?.data?.error || 'Error al cargar solicitudes.'); }
    finally { setLoading(false); }
  };

  const fetchProviders = async () => {
    try { const res = await api.get('/api/service-requests/providers'); setProviders(res.data); }
    catch { showToast("Error al cargar prestadores.", "error"); }
  };

  const handleStatusChange = async (id, status) => {
    if (status === 'assigned') { if (providers.length === 0) fetchProviders(); return; }
    try {
      await api.patch(`/api/service-requests/${id}/status`, { status });
      setRequests(prev => prev.map(r => r.id === id ? { ...r, status } : r));
      if (selected?.id === id) setSelected(prev => ({ ...prev, status }));
      showToast("Estado actualizado.");
    } catch { showToast("Error al actualizar estado.", "error"); }
  };

  const handleAssign = async (provider) => {
    if (!provider || !selected) return;
    setAssignLoading(true);
    try {
      await api.patch(`/api/service-requests/${selected.id}/assign`, { provider_id: provider.provider_id });
      setRequests(prev => prev.map(r => r.id === selected.id ? { ...r, status: 'assigned' } : r));
      setSelected(prev => ({ ...prev, status: 'assigned' }));
      showToast(`Asignada a ${provider.display_name}`);
    } catch (err) { showToast(err.response?.data?.error || "Error al asignar.", "error"); }
    finally { setAssignLoading(false); }
  };

  const filtered = useMemo(() => requests.filter(r => {
    const q = search.toLowerCase();
    const match = !q || r.user_name?.toLowerCase().includes(q) || r.user_email?.toLowerCase().includes(q) || getSvcName(r.service_key).toLowerCase().includes(q);
    return match && (!statusFilter || r.status === statusFilter);
  }), [requests, search, statusFilter]);

  const paginated  = filtered.slice(page * PAGE_SIZE, (page+1) * PAGE_SIZE);
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);

  const STATUS_FILTERS = [["","Todos"],["created","Creadas"],["assigned","Asignadas"],["completed","Completadas"],["cancelled","Canceladas"],["reviewing","En revisión"]];

  return (
    <>
      <div style={{ fontFamily: font, background: "#f5f7fa", minHeight: "100vh", padding: "22px 20px 50px" }}>
        <div style={{ maxWidth: 1300, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 260px", gap: 16, alignItems: "start" }}>

          {/* ── COLUMNA PRINCIPAL ───────────────────────────────────── */}
          <div>
            <div style={{ marginBottom: 14 }}>
              <h1 style={{ margin: "0 0 3px", fontSize: 19, fontWeight: 800, color: "#163244" }}>Solicitudes</h1>
              <p style={{ margin: 0, color: "#7a93a4", fontSize: 12 }}>{requests.length} solicitudes en total · {requests.filter(r=>r.status==='created').length} sin asignar</p>
            </div>

            {/* KPIs */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8, marginBottom: 12 }}>
              {[
                { label:"Total",      val: requests.length, col:"#163244", icon:"📋" },
                { label:"Asignadas",  val: requests.filter(r=>r.status==="assigned").length,  col:"#0F6E56", icon:"🤝" },
                { label:"Completadas",val: requests.filter(r=>r.status==="completed").length, col:"#1b7f4e", icon:"✅" },
                { label:"Canceladas", val: requests.filter(r=>r.status==="cancelled").length, col:"#c0392b", icon:"✕"  },
                { label:"Urgentes",   val: requests.filter(r=>r.request_type==="urgent").length, col:"#b06a00", icon:"🚨" },
              ].map(s => (
                <div key={s.label} style={{ background:"#fff", borderRadius:10, border:"1px solid rgba(0,0,0,0.07)", padding:"10px 12px" }}>
                  <div style={{ fontSize:9, fontWeight:700, color:"#7a93a4", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:4 }}>{s.icon} {s.label}</div>
                  <div style={{ fontSize:20, fontWeight:900, color:s.col, lineHeight:1 }}>{s.val}</div>
                </div>
              ))}
            </div>

            {/* Filtros */}
            <div style={{ display:"flex", gap:8, marginBottom:10, alignItems:"center", flexWrap:"wrap" }}>
              <input style={{ ...inputSx, flex:"1 1 200px", minWidth:0 }} placeholder="🔍  Nombre, email o servicio..." value={search} onChange={e=>{ setSearch(e.target.value); setPage(0); }} />
              <div style={{ display:"flex", gap:3, flexWrap:"wrap" }}>
                {STATUS_FILTERS.map(([v,l]) => (
                  <button key={v} onClick={()=>{ setStatusFilter(v); setPage(0); }} style={{ padding:"5px 11px", borderRadius:20, border:`1.5px solid ${statusFilter===v?"#163244":"rgba(0,0,0,0.1)"}`, background:statusFilter===v?"#163244":"#fff", color:statusFilter===v?"#fff":"#5a6e7f", fontFamily:font, fontWeight:600, fontSize:11, cursor:"pointer" }}>{l}</button>
                ))}
              </div>
              <button onClick={fetchRequests} style={{ marginLeft:"auto", padding:"6px 14px", borderRadius:50, background:"#163244", color:"#fff", border:"none", fontFamily:font, fontWeight:700, fontSize:11, cursor:"pointer" }}>↻ Actualizar</button>
            </div>

            {error && <div style={{ background:"rgba(192,57,43,0.07)", border:"1px solid rgba(192,57,43,0.2)", borderRadius:8, padding:"9px 12px", fontSize:12, color:"#c0392b", marginBottom:10 }}>⚠ {error}</div>}

            {/* Tabla */}
            <div style={{ background:"#fff", borderRadius:12, border:"1px solid rgba(0,0,0,0.07)", overflow:"hidden" }}>
              <div style={{ display:"grid", gridTemplateColumns:"50px 1.4fr 1.2fr 1.4fr 0.7fr 0.7fr 0.8fr 55px", padding:"9px 14px", background:"#163244", gap:6, alignItems:"center" }}>
                {["#","Servicio","Usuario","Email","Estado","Tipo","Fecha",""].map((h,i) => (
                  <div key={i} style={{ fontSize:9, fontWeight:700, color:"rgba(255,255,255,0.55)", textTransform:"uppercase", letterSpacing:"0.06em" }}>{h}</div>
                ))}
              </div>

              {loading && <div style={{ textAlign:"center", padding:"32px", color:"#7a93a4", fontSize:12 }}><span style={{ display:"inline-block", width:20, height:20, border:"2px solid #dce5ec", borderTopColor:"#163244", borderRadius:"50%", animation:"spin 0.7s linear infinite", marginBottom:8 }} /><br/>Cargando…</div>}

              {!loading && paginated.map((r, i) => {
                const common = parseData(r.common_data);
                const location = [common.ciudad, common.provincia].filter(Boolean).join(", ");
                return (
                  <div key={r.id}
                    style={{ display:"grid", gridTemplateColumns:"50px 1.4fr 1.2fr 1.4fr 0.7fr 0.7fr 0.8fr 55px", padding:"10px 14px", gap:6, alignItems:"center", borderBottom:i<paginated.length-1?"1px solid rgba(0,0,0,0.04)":"none", transition:"background 0.12s", cursor:"default" }}
                    onMouseEnter={e=>e.currentTarget.style.background="#fafcfe"}
                    onMouseLeave={e=>e.currentTarget.style.background="transparent"}
                  >
                    <div style={{ fontSize:10, color:"#aab8c2", fontWeight:600 }}>#{r.id}</div>
                    <div style={{ fontSize:11, fontWeight:700, color:"#163244", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{getSvcName(r.service_key)}</div>
                    <div>
                      <div style={{ fontSize:11, fontWeight:600, color:"#163244", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{r.user_name}</div>
                      {location && <div style={{ fontSize:9, color:"#aab8c2" }}>📍 {location}</div>}
                    </div>
                    <div style={{ fontSize:10, color:"#5a6e7f", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{r.user_email}</div>
                    <div><StatusBadge status={r.status} /></div>
                    <div style={{ fontSize:9, fontWeight:600, color: r.request_type==="urgent"?"#c0392b":"#163244" }}>{r.request_type==="urgent"?"🚨 Urgente":"💬 Cotiz."}</div>
                    <div style={{ fontSize:10, color:"#7a93a4" }}>{new Date(r.created_at).toLocaleDateString("es-AR")}</div>
                    <button onClick={()=>setSelected(r)} style={{ padding:"4px 10px", borderRadius:50, background:"#163244", border:"none", color:"#fff", fontFamily:font, fontWeight:600, fontSize:10, cursor:"pointer" }}>Ver →</button>
                  </div>
                );
              })}

              {!loading && paginated.length === 0 && <div style={{ textAlign:"center", padding:"32px", color:"#aab8c2", fontSize:12 }}>No se encontraron solicitudes</div>}
            </div>

            {/* Paginación */}
            {totalPages > 1 && (
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:10 }}>
                <div style={{ fontSize:11, color:"#7a93a4" }}>{filtered.length} solicitud{filtered.length!==1?"es":""} · pág. {page+1} de {totalPages}</div>
                <div style={{ display:"flex", gap:3 }}>
                  <button onClick={()=>setPage(p=>Math.max(0,p-1))} disabled={page===0} style={{ padding:"5px 12px", borderRadius:8, border:"1px solid rgba(0,0,0,0.1)", background:"#fff", color:page===0?"#ccc":"#163244", fontFamily:font, fontWeight:600, fontSize:11, cursor:page===0?"not-allowed":"pointer" }}>← Ant.</button>
                  {Array.from({length:Math.min(totalPages,7)},(_,i)=>{ const pg=totalPages<=7?i:Math.max(0,Math.min(page-3,totalPages-7))+i; return <button key={pg} onClick={()=>setPage(pg)} style={{ width:28, height:28, borderRadius:7, border:`1px solid ${pg===page?"#163244":"rgba(0,0,0,0.1)"}`, background:pg===page?"#163244":"#fff", color:pg===page?"#fff":"#163244", fontFamily:font, fontWeight:700, fontSize:11, cursor:"pointer" }}>{pg+1}</button>; })}
                  <button onClick={()=>setPage(p=>Math.min(totalPages-1,p+1))} disabled={page===totalPages-1} style={{ padding:"5px 12px", borderRadius:8, border:"1px solid rgba(0,0,0,0.1)", background:"#fff", color:page===totalPages-1?"#ccc":"#163244", fontFamily:font, fontWeight:600, fontSize:11, cursor:page===totalPages-1?"not-allowed":"pointer" }}>Sig. →</button>
                </div>
              </div>
            )}
          </div>

          {/* ── SIDEBAR ─────────────────────────────────────────────── */}
          <SidebarStats requests={requests} />
        </div>
      </div>

      {selected && <RequestDrawer req={selected} providers={providers} onClose={()=>setSelected(null)} onStatusChange={handleStatusChange} onAssign={handleAssign} assignLoading={assignLoading} />}
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={()=>setToast(null)} />}

      <style>{` * { box-sizing: border-box; } input:focus, select:focus { border-color: #163244 !important; outline: none; box-shadow: 0 0 0 3px rgba(22,50,68,0.08); } @keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
}
